/**
 * Hook for the payout approval dashboard.
 *
 * ## Approval flow (from this hook's perspective)
 *
 *   1. fetchPendingApprovals  — GET /submissions/approvals/pending
 *      Loads all records in `pending` status. Only superadmins and users with
 *      isFinanceApprover or isDeptHeadApprover can reach this endpoint (isPayoutApprover middleware).
 *
 *   2. stampApproval(submissionId)  — POST /submissions/:id/approval/stamp
 *      The server derives the approver's role from their user flags (finance vs. dept_head).
 *      If this stamp completes BOTH approvals, the server auto-triggers the Stripe transfer —
 *      no separate "execute" click is needed.
 *      onSuccess re-fetches the full list so paid/removed records disappear from the table.
 *
 *   3. rejectApproval(submissionId)  — POST /submissions/:id/approval/reject
 *      Marks the record as rejected. The brkfst employee who originally clicked "Pay Creator"
 *      can re-queue it by clicking Pay Creator again (createApprovalRecord clears the rejection).
 *      onSuccess re-fetches so the row disappears from the table.
 *
 *   4. bulkStampApprovals(submissionIds[]) / bulkRejectApprovals(submissionIds[])
 *      Fire N single-row requests with per-request toasts suppressed; one summary toast
 *      fires at the end. The caller is responsible for filtering out rows the approver
 *      has already stamped (see ApprovalDashboard's bulk-action handler).
 */
import { useDispatch, useSelector } from 'react-redux';

import { approvalsLoaded, selectApprovals } from 'features/approvals/approvals.slice';
import { apiAction } from 'shared/actions/api';
import { successToastActivated } from 'shared/actions/toasts';
import { RESOURCES } from 'shared/config/resourceNames';
import { API_APPROVAL_ROUTES } from 'shared/config/routes/api/apiApprovalRoutes';

interface StampOptions {
  /** Suppress the per-request success toast (used by the bulk paths). */
  silent?: boolean;
  /** Skip the auto re-fetch on success (used by bulk paths that re-fetch once at the end). */
  skipRefetch?: boolean;
}

export const useApprovals = () => {
  const dispatch = useDispatch();
  const approvals = useSelector(selectApprovals);

  const fetchPendingApprovals = () => {
    dispatch(
      apiAction({
        path: { route: API_APPROVAL_ROUTES.PENDING_APPROVALS },
        method: 'GET',
        entity: RESOURCES.SUBMISSION,
        successAction: approvalsLoaded,
      }),
    );
  };

  const stampApproval = (submissionId: number, options: StampOptions = {}) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.STAMP_APPROVAL,
          variables: { submissionId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.SUBMISSION,
        ...(options.silent ? {} : { successToast: { message: 'Approval stamped successfully' } }),
        ...(options.skipRefetch ? {} : { onSuccess: fetchPendingApprovals }),
      }),
    );
  };

  const rejectApproval = (submissionId: number, options: StampOptions = {}) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.REJECT_APPROVAL,
          variables: { submissionId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.SUBMISSION,
        ...(options.silent ? {} : { successToast: { message: 'Approval rejected' } }),
        ...(options.skipRefetch ? {} : { onSuccess: fetchPendingApprovals }),
      }),
    );
  };

  /**
   * Superadmin manual retry — POST /submissions/:id/approval/execute. Used when a Stripe
   * transfer failed and the record reverted to `pending` with both stamps already set, so
   * there is no completing-stamp to auto-trigger the payout. Mirrors stampApproval's shape;
   * onSuccess re-fetches so the paid row disappears.
   */
  const retryApproval = (submissionId: number) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.EXECUTE_PAYMENT,
          variables: { submissionId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.SUBMISSION,
        successToast: { message: 'Payment retried' },
        onSuccess: fetchPendingApprovals,
      }),
    );
  };

  /**
   * Bulk-stamp the given submissionIds. Each individual request is silent (no toast
   * per row) and skips auto-refetch; we dispatch one summary toast and one refetch at
   * the end. Caller is responsible for filtering out IDs the approver has already
   * stamped — this hook does not look at user flags.
   */
  const bulkStampApprovals = (submissionIds: number[], skippedCount = 0) => {
    if (!submissionIds.length && skippedCount === 0) return;
    submissionIds.forEach((id) => stampApproval(id, { silent: true, skipRefetch: true }));
    fetchPendingApprovals();
    const summary =
      skippedCount > 0
        ? `Stamped ${submissionIds.length}, skipped ${skippedCount} already-stamped.`
        : `Stamped ${submissionIds.length} approvals.`;
    dispatch(successToastActivated({ message: summary }));
  };

  const bulkRejectApprovals = (submissionIds: number[]) => {
    if (!submissionIds.length) return;
    submissionIds.forEach((id) => rejectApproval(id, { silent: true, skipRefetch: true }));
    fetchPendingApprovals();
    dispatch(successToastActivated({ message: `Rejected ${submissionIds.length} approvals.` }));
  };

  return {
    approvals,
    fetchPendingApprovals,
    stampApproval,
    rejectApproval,
    retryApproval,
    bulkStampApprovals,
    bulkRejectApprovals,
  };
};
